import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Alert, Button, CircularProgress, Card, CardContent,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { updatePaymentTrackingOnPageLoadUpfront } from '../../services/api';

const formatCurrency = (amount) => {
  if (!amount) return '₹0.00';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
};

const UpfrontPaymentResponse = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    handleResponse();
  }, []);

  const handleResponse = async () => {
    const params = {};
    for (let [key, value] of searchParams.entries()) {
      params[key.replace(/\+/g, ' ')] = decodeURIComponent(value);
    }

    const status = params.processedStatus?.toLowerCase() || 'failed';
    const message = decodeURIComponent(params.processedMessage || 'Payment response received.');

    const info = {
      responseCode: params['Response Code'] || params.ResponseCode || params.finalResponseCode,
      referenceNo: params.ReferenceNo || params.uniqueRefNumber,
      orderId: params.orderId || 'Unknown',
      amount: params.amount || '0',
      tid: params.tid || '',
      processedStatus: status,
      processedMessage: message,
    };

    setPaymentDetails(info);
    setPaymentStatus(status);

    try {
      const appId = localStorage.getItem('appId');
      const orgId = localStorage.getItem('orgId');
      await updatePaymentTrackingOnPageLoadUpfront({
        tid: info.tid,
        referenceNo: info.referenceNo,
        processedStatus: status,
        responseCode: info.responseCode,
        amount: info.amount,
        paymentMode: 'Online',
        orderId: info.orderId,
        processedMessage: message,
        appId,
        orgId,
      });
    } catch (err) {
      console.error('Error updating upfront payment tracking:', err);
    }

    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    localStorage.setItem('logout', Date.now());
    navigate('/main/login');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Processing payment response...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 5, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
          Upfront Fees Payment
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          NABL Accreditation — Upfront Fees Payment Response
        </Typography>
      </Paper>

      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {paymentStatus === 'success' ? (
          <Card elevation={3} sx={{ textAlign: 'center', p: 3 }}>
            <CardContent>
              <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ color: 'success.main', fontWeight: 'bold' }}>
                Payment Successful!
              </Typography>
              <Alert severity="success" sx={{ mb: 3 }}>
                {paymentDetails?.processedMessage || 'Your upfront fees payment has been completed successfully.'}
              </Alert>
              <Box sx={{ textAlign: 'left', backgroundColor: 'grey.50', p: 2, borderRadius: 1, mb: 3 }}>
                <Typography><strong>Reference No:</strong> {paymentDetails?.referenceNo || '—'}</Typography>
                <Typography><strong>Order ID:</strong> {paymentDetails?.orderId}</Typography>
                <Typography><strong>Amount:</strong> {formatCurrency(paymentDetails?.amount)}</Typography>
                <Typography><strong>Response Code:</strong> {paymentDetails?.responseCode}</Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<ReceiptIcon />}
                onClick={() => navigate('/main/dashboard')}
                sx={{ bgcolor: '#00024c', '&:hover': { bgcolor: '#1a237e' } }}
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card elevation={3} sx={{ textAlign: 'center', p: 3 }}>
            <CardContent>
              <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ color: 'error.main', fontWeight: 'bold' }}>
                Payment Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3 }}>
                {paymentDetails?.processedMessage || 'Your payment could not be processed.'}
              </Alert>
              <Box sx={{ textAlign: 'left', backgroundColor: 'grey.50', p: 2, borderRadius: 1, mb: 3 }}>
                <Typography><strong>Reference No:</strong> {paymentDetails?.referenceNo || '—'}</Typography>
                <Typography><strong>Order ID:</strong> {paymentDetails?.orderId}</Typography>
                <Typography><strong>Amount:</strong> {formatCurrency(paymentDetails?.amount)}</Typography>
                <Typography><strong>Response Code:</strong> {paymentDetails?.responseCode}</Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Please log out and try making the payment again.
              </Typography>
              <Button variant="contained" color="error" onClick={handleLogout}>
                Logout
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default UpfrontPaymentResponse;
